import { DOCUMENT, NgClass } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Inject, Input, OnDestroy, OnInit, Output, Renderer2, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { FuseConfig, FuseConfigService } from '@fuse/services/config';
import { FusePlatformService } from '@fuse/services/platform';
import { FUSE_VERSION } from '@fuse/version';
import { filter, Subject, Subscription, takeUntil } from 'rxjs';
import { Util } from 'app/models/util';
import { UserImpl } from 'app/models/user';
import { EventService } from 'app/services/event.service';
import { ModernWebLayoutComponent } from '../modern-web/modern-web.component';
import { FileManagerComponent } from 'app/modules/admin/apps/file-manager/file-manager.component';
import { LpmConfig } from 'app/models/lpm.config';
import { DmsClientTheme, DMS_THEME_CLASS_MAP } from 'app/models/dms-client-theme';
import { OverlayContainer } from '@angular/cdk/overlay';
import { ShadowDomOverlayContainer } from 'app/shared/custom-overlay-container';
import { DMSBaseServiceImp } from 'app/services/dms.base.service';
import { environment } from 'environments/environment';
import { UserService } from 'app/services/user.service';
import { ToastrService } from 'ngx-toastr';
import { AlertMessageService } from 'app/services/alert.message.service';
import { IAlert } from 'app/modules/admin/apps/file-manager/file-manager.types';
import { ExternalUser, WebComponentInputParams } from 'app/models/web-component-input-params.model';
import { DMS_INSTANCE_ID } from 'app/models/dms-instance.token';


@Component({
    selector: 'layout-web',
    templateUrl: './layout-web.component.html',
    styleUrls: ['./layout-web.component.scss'],
    encapsulation: ViewEncapsulation.ShadowDom,
    standalone: true,
    imports: [
        ModernWebLayoutComponent,
        FileManagerComponent,
        NgClass
    ],
    providers:[
        FileManagerComponent,
        DMSBaseServiceImp,
        { provide: DMS_INSTANCE_ID, useFactory: () => crypto.randomUUID() }
    ]
})
export class LayoutWebComponent implements OnInit, OnDestroy {

    @Input() defaultView: string = null;
    @Input() folderId : string = null;
    @Input() userId : string = null;
    @Input() lpmConfig : LpmConfig = new LpmConfig();
    @Output() externalSearchValue = new EventEmitter<string>();
    @Output() reflectiveResponse = new EventEmitter<any>();
    @Output() copilotToggle = new EventEmitter<boolean>();
    @Output() createEntityByCopilot = new EventEmitter<any>();

    @Input() externalUsers : ExternalUser[] = [];
    @Input() clientTheme: DmsClientTheme = null;
    view: string = null;
    config: FuseConfig;
    layout: string;
    scheme = 'light';
    theme = 'theme-default';
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private user = new UserImpl();
    private _schemeObserver: MutationObserver;
    last = null;

    subs = new Subscription();

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        @Inject(DOCUMENT) private _document: any,
        private _renderer2: Renderer2,
        private _router: Router,
        private _fuseConfigService: FuseConfigService,
        private _fusePlatformService: FusePlatformService,
        private util: Util,
        private eventServ: EventService,
        public _fileManagerComponent: FileManagerComponent,
        public _elementRef: ElementRef,
        public _userService: UserService,
        private toastrService: ToastrService,
        private alertService: AlertMessageService,
        private _overlayContainer: OverlayContainer,
        @Inject(DMS_INSTANCE_ID) private instanceId: string


    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------
    @HostListener('window:popstate', ['$event'])
    onBrowserBackBtnClose(event: Event) {
    this.showUploadAlert();    
    }

    @HostListener('window:pushstate', ['$event'])
    onBrowserFwdBtnClose(event: Event) {
    this.showUploadAlert();    
    }

    /**
     * On init
     */
    ngOnInit(): void {
        window.addEventListener('beforeunload', this.onBeforeUnload);
        this.alertService.getService().subscribe((alert: IAlert) => {
            if (alert) this.toastrService.error(alert.message, 'Error', { timeOut: 8000 });
        });
        //event servoce for webcomponent
        //event service for webcomponent
        this.appendFontFaceLink();
        this._syncSchemeFromBody();
        this._observeBodyScheme();
        this.applyClientStyles();
        this.setCdkExtraContainerStyle();
        this.eventServ.searchContacts.subscribe((value)=>{
             if (value) {
                this.externalSearchValue.emit(value)
             }
        })

        this.eventServ.sendResponseToLolly.subscribe((res)=>{
            if (res) {
               this.reflectiveResponse.emit(res)
            }
       })

       this.eventServ.createEntityFromCopilot.subscribe((value)=>{
        if (value) {
           this.createEntityByCopilot.emit(value)
        }
   })

        this.user = this.util.getUserFromSession();
        this.eventServ.onThemePrefChange.subscribe((user) => {
            if (user) this.user = user;
            this.setPreference();
        })

        this.setPreference();
        // Store the options

        // Update the scheme and theme
        // Subscribe to config changes
        this._fuseConfigService.config$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((config: FuseConfig) => {
                // Store the config
                this.config = config;

                // Update the layout
                this._updateLayout();
            });

        // Subscribe to NavigationEnd event
        this._router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            takeUntil(this._unsubscribeAll),
        ).subscribe(() => {
            // Update the layout
            this._updateLayout();
        });

        // Set the app version
        this._renderer2.setAttribute(this._document.querySelector('[ng-version]'), 'fuse-version', FUSE_VERSION);

        // Set the OS name
        this._renderer2.addClass(this._document.body, this._fusePlatformService.osName);
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this.subs.unsubscribe();
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
        if (this._schemeObserver) {
            this._schemeObserver.disconnect();
        }
        this.util.clearWebComponentInputParams(this.instanceId);
    }

    setPreference() {
        //Lolly V3 Hyboria uses same theme convention as DMS. When DMS is introduced as WC, it overrides Lolly theme with these functions. 
        //Therefore, Lolly theme will be dominant for now, until we decouple by making DMS have unique theme convention. Ex. theme-purple -> theme-purple-dms
        /*
        this.scheme = this.user && this.user.scheme ? this.user.scheme : this.scheme;
        this.theme = this.user && this.user.theme ? this.user.theme : this.theme;
        */
        this._updateScheme();
        this._updateTheme();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Update the selected layout
     */
    private _updateLayout(): void {
        // Get the current activated route
        let route = this._activatedRoute;
        while (route.firstChild) {
            route = route.firstChild;
        }

        // 1. Set the layout from the config
        this.layout = this.config.layout;

        // 2. Get the query parameter from the current route and
        // set the layout and save the layout to the config
        const layoutFromQueryParam = route.snapshot.queryParamMap.get('layout');
        if (layoutFromQueryParam) {
            this.layout = layoutFromQueryParam;
            if (this.config) {
                this.config.layout = layoutFromQueryParam;
            }
        }

        // 3. Iterate through the paths and change the layout as we find
        // a config for it.
        //
        // The reason we do this is that there might be empty grouping
        // paths or componentless routes along the path. Because of that,
        // we cannot just assume that the layout configuration will be
        // in the last path's config or in the first path's config.
        //
        // So, we get all the paths that matched starting from root all
        // the way to the current activated route, walk through them one
        // by one and change the layout as we find the layout config. This
        // way, layout configuration can live anywhere within the path and
        // we won't miss it.
        //
        // Also, this will allow overriding the layout in any time so we
        // can have different layouts for different routes.
        const paths = route.pathFromRoot;
        paths.forEach((path) => {
            // Check if there is a 'layout' data
            if (path.routeConfig && path.routeConfig.data && path.routeConfig.data.layout) {
                // Set the layout
                this.layout = path.routeConfig.data.layout;
            }
        });
    }

    /**
     * Update the selected scheme
     *
     * @private
     */
    private _updateScheme(): void {
        // If the dom body already has a valid scheme, do nothing. Allow LPM host to set scheme
        if (this._document.body.classList.contains('dark') || this._document.body.classList.contains('light')) {
            return;
        }
        // Remove class names for all schemes
        this._document.body.classList.remove('light', 'dark');

        // Add class name for the currently selected scheme
        this._document.body.classList.add(this.scheme);
    }

    /**
     * Update the selected theme
     *
     * @private
     */
    private _updateTheme(): void {
        // If the dom body already has a valid theme, do nothing. Allow LPM host to set theme
        const hasThemeClass = Array.from(this._document.body.classList).some((className: string) => className.startsWith('theme-'));
        if (hasThemeClass) {
            return;
        }
        // Find the class name for the previously selected theme and remove it
        this._document.body.classList.forEach((className: string) => {
            if (className.startsWith('theme-')) {
                this._document.body.classList.remove(className, className.split('-')[1]);
            }
        });

        // Add class name for the currently selected theme
        this._document.body.classList.add(this.theme);
    }

    ngOnChanges(changes: SimpleChanges): void {
        console.log('[DMS] ngOnChanges', {
            defaultView: this.defaultView,
            folderId: this.folderId,
            userId: this.userId,
            lpmConfig: this.lpmConfig,
            externalUsers: this.externalUsers,
        });
        this.view = this.defaultView;
        this.util.setRootInSession(this.folderId);
        this.util.setUserId(this.userId);
        this.util.setIsWebApp(true);
        this.util.setUserType(this.lpmConfig.scope)
        let addType = new DMSBaseServiceImp(this.lpmConfig.scope);
        this.getAndSetUser();

        // this.util.setScope(this.lpmConfig.scope);

        for (let propName in changes) {
            let changedProp = changes[propName];
            let to = changedProp.currentValue; //JSON.stringify();
            if (changedProp.isFirstChange()) {
            } else {
                let from = JSON.stringify(changedProp.previousValue);
                if (`${propName}` == `externalUsers`) {
                    this.externalUsers = to;
                    this.eventServ.callBack.emit(this.externalUsers);
                }
            }
        }

        this.setWebInputParams();

        // host change may change default theme
        if (changes.clientTheme || changes.lpmConfig) {
            this.applyClientStyles();
        }
    }

    /** clientTheme merged with host defaults. */
    private get _resolvedTheme(): DmsClientTheme | null {
        return this.util.getWebComponentInputParams(this.instanceId)?.clientTheme ?? null;
    }

    private applyClientStyles(): void {
        const clientTheme = this._resolvedTheme;
        if (!clientTheme?.name) {
            this._syncSchemeFromBody();
            return;
        }

        const shadowRoot = this._elementRef.nativeElement.shadowRoot;
        // Always remove the override style so stale font/radius rules don't linger.
        shadowRoot?.querySelector('#dms-client-overrides')?.remove();

        const { name, fontFamily, buttonBorderRadius } = clientTheme;
        const themeClass = DMS_THEME_CLASS_MAP[name];
        if (themeClass) {
            this.theme = themeClass;
            if (this._overlayContainer instanceof ShadowDomOverlayContainer) {
                this._overlayContainer.setClientThemeClass(themeClass);
            }
        } else {
            // unmapped preset, keep body theme
            this._syncSchemeFromBody();
        }

        if (!shadowRoot) return;

        const styles: string[] = [];

        if (fontFamily) {
            this._loadGoogleFont(fontFamily);
            styles.push(`* { font-family: '${fontFamily}', sans-serif !important; }`);
        }

        if (buttonBorderRadius) {
            styles.push(`
                .mdc-button, .mat-mdc-button, .mat-mdc-raised-button,
                .mat-mdc-flat-button, .mat-mdc-outlined-button,
                .mat-mdc-icon-button { border-radius: ${buttonBorderRadius} !important; }
            `);
        }

        if (styles.length > 0) {
            const styleEl = document.createElement('style');
            styleEl.id = 'dms-client-overrides';
            styleEl.textContent = styles.join('\n');
            shadowRoot.appendChild(styleEl);
        }
    }

    private _loadGoogleFont(fontFamily: string): void {
        const fontId = `dms-font-${fontFamily.replace(/\s+/g, '-').toLowerCase()}`;
        if (document.getElementById(fontId)) return;
        const link = document.createElement('link');
        link.id = fontId;
        link.rel = 'stylesheet';
        link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontFamily)}:wght@300;400;500;600;700&display=swap`;
        document.head.appendChild(link);
    }

    appendFontFaceLink() {
        const links = [
            { rel: 'preconnect', href: 'https://fonts.gstatic.com' },
            { rel: 'stylesheet', href: `${environment.assetsBaseUrl}/fonts/inter/inter.css` },
            { rel: 'stylesheet', href: 'https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:ital,wght@0,400;0,500;0,600;1,400&display=swap' },
            { rel: 'stylesheet', href: 'https://fonts.googleapis.com/icon?family=Material+Icons' },
        ];
    
        const shadowRoot = this._elementRef.nativeElement.shadowRoot;
        const target =  document.head;
    
        links.forEach(({ rel, href }) => {
            const link = document.createElement('link');
            link.rel = rel;
            link.href = href;
            target.appendChild(link);
        });
    }

    private setCdkExtraContainerStyle(): void {
        const style = document.createElement('style');
        style.textContent = `
          .cdk-describedby-message-container {
            display: none;
          }
        `;
        document.head.appendChild(style);
      }

    /**
     * Reads the current scheme (light/dark) from document.body and applies it
     * to the component's internal `scheme` property.
     */
    private _syncSchemeFromBody(): void {
        const body = this._document.body;
        this.scheme = body.classList.contains('dark') ? 'dark' : 'light';
        // When clientTheme is set, the theme class is owned by applyClientStyles — never read it back from body.
        if (!DMS_THEME_CLASS_MAP[this._resolvedTheme?.name]) {
            body.classList.forEach((className: string) => {
                if (className.startsWith('theme-')) {
                    this.theme = className;
                }
            });
        }
    }

    /**
     * Observes document.body class mutations so that when the host application
     * toggles light/dark, the Shadow DOM wrapper div reflects the change too.
     */
    private _observeBodyScheme(): void {
        this._schemeObserver = new MutationObserver(() => {
            this._syncSchemeFromBody();
        });
        this._schemeObserver.observe(this._document.body, {
            attributes: true,
            attributeFilter: ['class'],
        });
    }

    getAndSetUser(){
        if(!this.userId) return;
        this._userService.getUserById(this.userId).then(response=>{
            this.user = this._userService.getData(response);
            this.util.setUserInSession(this.user);
        })
    }

    onBeforeUnload = (event: BeforeUnloadEvent) => {
        if (this.util.fileUploader?.isUploading) {
            event.preventDefault();
            event.returnValue = '';
        }
    };

    showUploadAlert(): void {
        if (this.util?.fileUploader?.isUploading) {
            alert("Leaving this page may cancel your upload.");
        }
    }

    setWebInputParams(){
        const inputParams: WebComponentInputParams = new WebComponentInputParams();
        inputParams.folderId = this.folderId;
        inputParams.userId = this.userId;
        inputParams.lpmConfig = this.lpmConfig;
        inputParams.externalUsers = this.externalUsers;
        inputParams.clientTheme = this.clientTheme ?? new DmsClientTheme();
        this.util.setWebComponentInputParams(inputParams, this.instanceId);
    }

}
